import React, { useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';

interface ReactivationModalProps {
  isOpen: boolean;
  onReactivate: () => Promise<void>;
  onLogout: () => void;
}

/**
 * Modal shown when a user with a deleted portfolio tries to log in.
 * Offers option to reactivate and start fresh, or log out.
 */

const ReactivationModal: React.FC<ReactivationModalProps> = ({
  isOpen,
  onReactivate,
  onLogout,
}) => {
  const [isReactivating, setIsReactivating] = useState(false);

  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      await onReactivate();
    } finally {
      setIsReactivating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} title="Welcome Back!" size="md">
      <div className="space-y-4">
        <p className="text-gray-700">
          Your portfolio was previously deleted. Would you like to reactivate your account?
        </p>
        
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-sm text-yellow-700">
            <strong>Note:</strong> You'll need to start from scratch. All your previous portfolio data has been permanently deleted.
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onLogout}
            disabled={isReactivating}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No, log out
          </button>
          <button
            onClick={handleReactivate}
            disabled={isReactivating}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isReactivating && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            Yes, reactivate my portfolio
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReactivationModal;
